import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
#handleProductSelect = (event) => {
  if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

  event.promise
    .then(({ detail }) => {
      if (!detail?.html) return;

      const { html, resource, newProduct } = detail;

      // 如果是 combined listing 切换了完全不同的产品，仍然整体替换
      if (newProduct) {
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;
        this.replaceWith(newMediaGallery);
        return;
      }

      // 获取变体对应的 featured media ID
      const featuredMediaId = resource?.featured_media?.id;
      if (!featuredMediaId) return;

      // 在当前 gallery 中找到对应的 media 元素
      const targetMedia = this.querySelector(`[data-media-id="${featuredMediaId}"]`);
      if (!targetMedia || !this.slideshow) return;

      // 找到该 media 在 slideshow 中的 index
      const allMedia = Array.from(this.querySelectorAll('[data-media-id]'));
      const index = allMedia.indexOf(targetMedia);
      if (index === -1) return;

      // 滑动到对应图片
      this.slideshow.select(index, undefined, { animate: true });
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
    });
};
  

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
